import React, { useContext } from 'react';
import { config } from '@xingternal/hops';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import * as Types from '@xingternal/pages-graphql-types';
import { useHistory } from 'react-router';

import { Button, TextButton } from '@xingternal/button';
import {
  IconArrowLeft,
  IconInfo,
  IconStatistics,
  IconEdit,
} from '@xingternal/icons';
import { BodyCopy } from '@xingternal/typography';
import { OmView } from '@xingternal/platform-layout-om';
import { XingFrameContext } from '@xingternal/xing-frame-provider';

import { usePageContext, useEditContext } from '@xingternal/pages-common';
import {
  trackIsEditingToggle,
  trackOpenNavInfoBox,
  getTrackingUrl,
} from '@xingternal/pages-tracking';
import PageInfo from './PageInfo';
import { XBM_STATISTICS_URL, PROMOTE_URL } from '../../config';
import { useResizeObserver, useStickyBarWithOffset } from '../../hooks';
import { CommboxButton } from '@xingternal/pages-news';
import { mergeRefs } from './helpers';
import styles from './EditBar.css';

type ToogleEditModeProps = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
};

type ManageUpdatesProps = {
  basePath: string;
  pageSlug: string;
  isArmstrong?: boolean;
};

type EditBarBackProps = {
  isArmstrong?: boolean;
};

type EditBarProps = {
  pageSlug: string;
  goBack?: () => void;
  displayNewsButtons: boolean;
  globalId: string;
  displayBackButton: boolean;
  contractType?: Types.Maybe<Types.EntityPageContractType>;
  canEditOnMobile?: boolean;
  displayManageUpdates?: boolean;
  logo?: string;
  pageId?: string;
  pageTitle?: string;
};

const ToggleEditMode = ({ isEditing, setIsEditing }: ToogleEditModeProps) => (
  <Button
    variant={isEditing ? 'secondary' : 'primary'}
    size={'small'}
    onClick={() => {
      setIsEditing(!isEditing);
      trackIsEditingToggle(!isEditing);
    }}
    className={styles.button}
    data-testid={
      isEditing
        ? 'EP_EDIT_ACTION_SET_EDIT_MODE_OFF'
        : 'EP_EDIT_ACTION_SET_EDIT_MODE_ON'
    }
  >
    <FormattedMessage
      id={
        isEditing
          ? 'EP_EDIT_ACTION_SET_EDIT_MODE_OFF'
          : 'EP_EDIT_ACTION_SET_EDIT_MODE_ON'
      }
    />
  </Button>
);

const ManageUpdates = ({
  isArmstrong,
  basePath,
  pageSlug,
}: ManageUpdatesProps) => (
  <Button
    className={cx({
      [styles.isArmstrong]: isArmstrong,
    })}
    variant={'secondary'}
    size={'small'}
    to={`${basePath}/${pageSlug}/news`}
  >
    <FormattedMessage id="EP_EDITBAR_MANAGE_UPDATES" />
  </Button>
);

const EditBarBack = ({ isArmstrong }: EditBarBackProps) => {
  let history = useHistory();
  const { pageContext } = usePageContext();

  return (
    <TextButton
      size={'medium'}
      icon={IconArrowLeft}
      onClick={() =>
        history.push(pageContext.basePath + '/' + pageContext.pageSlug)
      }
      data-cy="back_to_overview"
      className={cx({
        [styles.isArmstrong]: isArmstrong,
      })}
    >
      <FormattedMessage
        id={'EP_SUBPAGE_BACK'}
        defaultMessage={'EP_SUBPAGE_BACK'}
      />
    </TextButton>
  );
};

const EditBar = ({
  goBack,
  pageSlug,
  displayNewsButtons,
  contractType,
  globalId,
  displayBackButton,
  canEditOnMobile = false,
  displayManageUpdates = false,
}: EditBarProps) => {
  const { isEditor, isEditing, setIsEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const { featureSwitches } = useContext(XingFrameContext);

  const hasCanEditMobileFeatureSwitch =
    featureSwitches?.includes('ep_mobileEdit');

  const isArmstrong =
    featureSwitches?.filter((x) => x === 'armstrong_v1').length > 0;

  const [ref, entry] = useResizeObserver();
  const [stickyBarRef, stickyBarFixed] = useStickyBarWithOffset();

  if (!isEditor) {
    return null;
  }

  return (
    <>
      <div
        className={cx(styles.editBarMobile, {
          [styles.displayBackButton]: displayBackButton,
          [styles.displayCta]: canEditOnMobile || displayManageUpdates,
        })}
        data-testid="edit-bar-mobile"
      >
        <div className={styles.bannerMobileContainer}>
          <div
            className={cx(styles.bannerMobile, {
              [styles.armstrongEditBarMobile]: isArmstrong,
            })}
          >
            {displayBackButton && goBack && (
              <div
                className={cx(styles.backButtonContainer, {
                  [styles.isArmstrong]: isArmstrong,
                })}
              >
                <EditBarBack />
              </div>
            )}
            {(canEditOnMobile || (displayManageUpdates && !isArmstrong)) && (
              <div className={styles.ctaContainer}>
                {canEditOnMobile && (
                  <ToggleEditMode
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                  />
                )}
                {displayManageUpdates && (
                  <ManageUpdates
                    basePath={pageContext?.basePath}
                    isArmstrong={isArmstrong}
                    pageSlug={pageSlug}
                  />
                )}
              </div>
            )}
            {
              <div className={styles.labelContainer}>
                {canEditOnMobile && hasCanEditMobileFeatureSwitch ? (
                  <IconInfo
                    height={24}
                    width={24}
                    className={styles.editMobileIcon}
                  />
                ) : (
                  <IconEdit
                    height={24}
                    width={24}
                    className={styles.editMobileIcon}
                  />
                )}
                <BodyCopy size={'small'} className={styles.mobileInfo}>
                  <span data-testid="edit-bar-mobile-label">
                    <FormattedMessage
                      id={
                        canEditOnMobile && hasCanEditMobileFeatureSwitch
                          ? isEditing
                            ? 'EP_EDIT_INFO_EDIT_VIEW'
                            : 'EP_EDIT_INFO_VISITOR_VIEW'
                          : 'EP_EDIT_INFO_MOBILE'
                      }
                      values={{
                        span: (msg: string) => <span>{msg}</span>,
                      }}
                    />
                  </span>
                </BodyCopy>
              </div>
            }
          </div>
        </div>
      </div>

      <div
        data-testid="edit-bar-desktop"
        className={cx(styles.editBar, {
          [styles.isSticky]:
            pageContext.isShowingCompanyBanner && stickyBarFixed,
        })}
        ref={mergeRefs([ref, stickyBarRef])}
      >
        <div
          className={cx(styles.editBarSticky, {
            [styles.isEditing]: isEditing,
            [styles.isShowingCompanyBanner]:
              pageContext.isShowingCompanyBanner && !stickyBarFixed,
          })}
          style={
            typeof entry !== 'undefined' && entry.contentRect.width > 0
              ? { width: entry.contentRect?.width }
              : { visibility: 'hidden' }
          }
        >
          {!displayBackButton && (
            <div className={styles.infoContainer}>
              <IconInfo height={24} width={24} />
              <BodyCopy size={'small'} className={styles.text}>
                <FormattedMessage
                  id={
                    isEditing
                      ? 'EP_EDIT_INFO_EDIT_VIEW'
                      : 'EP_EDIT_INFO_VISITOR_VIEW'
                  }
                />
              </BodyCopy>
            </div>
          )}
          {displayBackButton && goBack && (
            <EditBarBack isArmstrong={isArmstrong} />
          )}
          <div className={styles.actionContainer}>
            {!isEditing && displayNewsButtons && <CommboxButton />}
            {isEditing && (
              <>
                <OmView
                  trigger={(activateOmView: () => void) => (
                    <Button
                      icon={IconInfo}
                      size={'small'}
                      variant={'secondary'}
                      className={styles.button}
                      data-testid={'EP_EDIT_PAGE_INFO_BUTTON'}
                      onClick={() => {
                        trackOpenNavInfoBox();
                        activateOmView();
                      }}
                      aria-label="Info"
                    />
                  )}
                >
                  <PageInfo contractType={contractType} pageSlug={pageSlug} />
                </OmView>
                <Button
                  icon={IconStatistics}
                  size={'small'}
                  variant={'secondary'}
                  className={styles.button}
                  href={getTrackingUrl({
                    to: XBM_STATISTICS_URL(pageSlug),
                    trackingKey: 'viewXBM',
                  })}
                  target={'_blank'}
                  aria-label="Statistics"
                />
                <Button
                  variant={'secondary'}
                  size={'small'}
                  className={styles.button}
                  href={getTrackingUrl({
                    to: PROMOTE_URL(config.apiHost, globalId),
                    trackingKey: 'viewPagePromote',
                  })}
                  target={'_blank'}
                >
                  <FormattedMessage id={'EP_EDIT_ACTION_PROMOTE'} />
                </Button>
              </>
            )}
            <ToggleEditMode isEditing={isEditing} setIsEditing={setIsEditing} />
          </div>
        </div>
      </div>
    </>
  );
};

export default EditBar;
